import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Users, 
  Package, 
  RefreshCw,
  Lock,
  Unlock,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Building2,
  Rows3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TrafficControlDashboard = () => {
  const navigate = useNavigate();
  const { section: sectionSlug } = useParams();
  const sectionQs = sectionSlug ? `?section=${encodeURIComponent(sectionSlug)}` : '';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hallsOverview, setHallsOverview] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({
    total_active: 0,
    put_away_active: 0,
    picking_active: 0,
    manual_blocks: 0,
    block_putaway: 0,
    block_picking: 0
  });
  const [selectedHall, setSelectedHall] = useState(null);
  const [hallRows, setHallRows] = useState([]);
  
  // Manual reservation dialog
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [reserveHall, setReserveHall] = useState('');
  const [reserveRow, setReserveRow] = useState('');
  const [reserveReason, setReserveReason] = useState('');
  const [reserveType, setReserveType] = useState('block_putaway');
  
  // Auto-refresh interval (30 seconds)
  const AUTO_REFRESH_INTERVAL = 30000;

  const fetchData = useCallback(async (showLoadingState = true) => {
    try {
      if (showLoadingState) setRefreshing(true);
      
      const token = localStorage.getItem('token');
      
      // Fetch halls overview and reservations in parallel
      const [hallsRes, reservationsRes] = await Promise.all([
        fetch(`${API}/api/wms/traffic/halls-overview${sectionQs}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/wms/traffic/reservations${sectionQs}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (hallsRes.ok) {
        const hallsData = await hallsRes.json();
        setHallsOverview(hallsData.halls || []);
      }
      
      if (reservationsRes.ok) {
        const reservationsData = await reservationsRes.json();
        setReservations(reservationsData.reservations || []);
        setStats(reservationsData.stats || {
          total_active: 0,
          put_away_active: 0,
          picking_active: 0,
          manual_blocks: 0,
          block_putaway: 0,
          block_picking: 0
        });
      }
    } catch (error) {
      console.error('Error fetching traffic data:', error);
      toast.error('Failed to fetch traffic data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sectionQs]);

  const fetchHallDetails = async (hallName) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/wms/traffic/reservations/hall/${hallName}${sectionQs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHallRows(data.rows || []);
        setSelectedHall(hallName);
      }
    } catch (error) {
      console.error('Error fetching hall details:', error);
      toast.error('Failed to fetch hall details');
    }
  };

  const handleManualReserve = async () => {
    if (!reserveHall || !reserveRow) {
      toast.error('Please enter hall and row');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/wms/traffic/reservations/manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hall: reserveHall,
          row: reserveRow,
          operation_type: reserveType,
          reason: reserveReason
        })
      });
      
      if (res.ok) {
        toast.success(`Row ${reserveHall}-${reserveRow} reserved`);
        setShowReserveDialog(false);
        setReserveHall('');
        setReserveRow('');
        setReserveReason('');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reserve row');
      }
    } catch (error) {
      console.error('Error reserving row:', error);
      toast.error('Failed to reserve row');
    }
  };

  const handleReleaseRow = async (hall, row) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/wms/traffic/reservations/${hall}/${row}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Row ${hall}-${row} released`);
        fetchData();
        if (selectedHall === hall) {
          fetchHallDetails(hall);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to release row');
      }
    } catch (error) {
      console.error('Error releasing row:', error);
      toast.error('Failed to release row');
    }
  };

  const handleExtendReservation = async (hall, row) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/wms/traffic/reservations/${hall}/${row}/extend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ minutes: 15 })
      });
      
      if (res.ok) {
        toast.success(`Reservation extended by 15 minutes`);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to extend reservation');
      }
    } catch (error) {
      console.error('Error extending reservation:', error);
      toast.error('Failed to extend reservation');
    }
  };

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchData();
    
    const interval = setInterval(() => {
      fetchData(false);
    }, AUTO_REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [fetchData]);

  const getOperationBadge = (operationType) => {
    switch (operationType) {
      case 'put_away':
        return <Badge className="bg-blue-500 text-white">Put Away</Badge>;
      case 'picking':
        return <Badge className="bg-green-500 text-white">Picking</Badge>;
      case 'manual_block':
        return <Badge className="bg-red-500 text-white">Blocked (All)</Badge>;
      case 'block_putaway':
        return <Badge className="bg-orange-500 text-white">No Put-Away</Badge>;
      case 'block_picking':
        return <Badge className="bg-purple-500 text-white">No Picking</Badge>;
      default:
        return <Badge variant="secondary">{operationType}</Badge>;
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/dispatch')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <MapPin className="w-6 h-6 text-orange-400" />
              Traffic Control Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData()}
              disabled={refreshing}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setShowReserveDialog(true)}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="manual-reserve-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Reserve Row
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-white shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Active</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="stat-total">{stats.total_active}</p>
                </div>
                <Users className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Put Away</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="stat-putaway">{stats.put_away_active}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Picking</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="stat-picking">{stats.picking_active}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">No Put-Away</p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="stat-block-putaway">{stats.block_putaway || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">No Picking</p>
                  <p className="text-2xl font-bold text-purple-600" data-testid="stat-block-picking">{stats.block_picking || 0}</p>
                </div>
                <Lock className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Blocked All</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="stat-blocks">{stats.manual_blocks}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Halls Overview */}
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5" />
                Halls Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hallsOverview.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No FG halls configured</p>
              ) : (
                <div className="space-y-3">
                  {hallsOverview.map((hall) => (
                    <div 
                      key={hall.hall_name}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedHall === hall.hall_name 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                      onClick={() => fetchHallDetails(hall.hall_name)}
                      data-testid={`hall-${hall.hall_name}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{hall.hall_name}</h3>
                          <p className="text-sm text-gray-500">{hall.description || 'FG Storage'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">
                            <span className="text-green-600 font-medium">{hall.available_rows}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-gray-600">{hall.total_rows}</span>
                            <span className="text-gray-400 text-xs ml-1">rows free</span>
                          </p>
                        </div>
                      </div>
                      
                      {hall.reserved_rows > 0 && (
                        <div className="flex gap-2 mt-2">
                          {hall.put_away_active > 0 && (
                            <Badge variant="outline" className="text-blue-600 border-blue-300">
                              {hall.put_away_active} Put Away
                            </Badge>
                          )}
                          {hall.picking_active > 0 && (
                            <Badge variant="outline" className="text-green-600 border-green-300">
                              {hall.picking_active} Picking
                            </Badge>
                          )}
                          {hall.manual_blocks > 0 && (
                            <Badge variant="outline" className="text-red-600 border-red-300">
                              {hall.manual_blocks} Blocked
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Reservations */}
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Rows3 className="w-5 h-5" />
                Active Reservations
                <Badge variant="secondary" className="ml-auto">{reservations.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reservations.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No active reservations</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {reservations.map((res, idx) => (
                    <div 
                      key={`${res.hall}-${res.row}-${idx}`}
                      className="p-3 border border-gray-200 rounded-lg"
                      data-testid={`reservation-${res.hall}-${res.row}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-gray-900">
                            {res.hall}-{res.row}
                          </span>
                          {getOperationBadge(res.operation_type)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExtendReservation(res.hall, res.row)}
                            className="h-7 px-2"
                            title="Extend 15 min"
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReleaseRow(res.hall, res.row)}
                            className="h-7 px-2 text-red-500 hover:text-red-700"
                            title="Release"
                          >
                            <Unlock className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p><strong>Staff:</strong> {res.staff_name || res.staff_id}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3" />
                          <span className={`font-medium ${
                            formatTimeRemaining(res.expires_at) === 'Expired' ? 'text-red-500' : 'text-orange-500'
                          }`}>
                            {formatTimeRemaining(res.expires_at)}
                          </span>
                        </div>
                        {res.reason && (
                          <p className="mt-1 text-gray-500"><strong>Reason:</strong> {res.reason}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Selected Hall Detail */}
        {selectedHall && (
          <Card className="bg-white shadow-md mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5" />
                Hall {selectedHall} - Row Status
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => setSelectedHall(null)}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hallRows.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No rows found for this hall</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {hallRows.map((row) => (
                    <div
                      key={row.row_name}
                      className={`p-2 rounded-md text-center text-sm font-medium transition-all ${
                        row.is_reserved
                          ? row.reservation?.operation_type === 'picking'
                            ? 'bg-green-100 border-2 border-green-500 text-green-800'
                            : row.reservation?.operation_type === 'put_away'
                            ? 'bg-blue-100 border-2 border-blue-500 text-blue-800'
                            : row.reservation?.operation_type === 'block_putaway'
                            ? 'bg-orange-100 border-2 border-orange-500 text-orange-800'
                            : row.reservation?.operation_type === 'block_picking'
                            ? 'bg-purple-100 border-2 border-purple-500 text-purple-800'
                            : 'bg-red-100 border-2 border-red-500 text-red-800'
                          : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={row.is_reserved ? `${row.reservation?.operation_type}: ${row.reservation?.staff_name}` : 'Available'}
                      data-testid={`row-${selectedHall}-${row.row_name}`}
                    >
                      {row.row_name}
                      {row.is_reserved && (
                        <div className="text-xs mt-1 truncate">
                          {row.reservation?.operation_type === 'picking' ? '🛒' : 
                           row.reservation?.operation_type === 'put_away' ? '📦' : 
                           row.reservation?.operation_type === 'block_putaway' ? '🚫📦' :
                           row.reservation?.operation_type === 'block_picking' ? '🚫🛒' : '⛔'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></div>
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 rounded"></div>
                  <span>Put Away</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 border-2 border-green-500 rounded"></div>
                  <span>Picking</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-100 border-2 border-orange-500 rounded"></div>
                  <span>No Put-Away</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-100 border-2 border-purple-500 rounded"></div>
                  <span>No Picking</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 border-2 border-red-500 rounded"></div>
                  <span>Blocked All</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Manual Reserve Dialog */}
      <Dialog open={showReserveDialog} onOpenChange={setShowReserveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reserve Row</DialogTitle>
            <DialogDescription>
              Manually reserve or block a row for maintenance or other reasons.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hall">Hall</Label>
                <Input
                  id="hall"
                  placeholder="e.g., H06"
                  value={reserveHall}
                  onChange={(e) => setReserveHall(e.target.value.toUpperCase())}
                  data-testid="reserve-hall-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="row">Row</Label>
                <Input
                  id="row"
                  placeholder="e.g., A"
                  value={reserveRow}
                  onChange={(e) => setReserveRow(e.target.value.toUpperCase())}
                  data-testid="reserve-row-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">Reservation Type</Label>
              <Select value={reserveType} onValueChange={setReserveType}>
                <SelectTrigger data-testid="reserve-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="block_putaway">🚫 Block Put-Away Only (Allow Picking)</SelectItem>
                  <SelectItem value="block_picking">🚫 Block Picking Only (Allow Put-Away)</SelectItem>
                  <SelectItem value="manual_block">⛔ Block ALL Operations</SelectItem>
                  <SelectItem value="put_away">📦 Simulate Put-Away Activity</SelectItem>
                  <SelectItem value="picking">🛒 Simulate Picking Activity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Input
                id="reason"
                placeholder="e.g., Maintenance, Inventory count"
                value={reserveReason}
                onChange={(e) => setReserveReason(e.target.value)}
                data-testid="reserve-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReserveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleManualReserve} data-testid="confirm-reserve-btn">
              Reserve Row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrafficControlDashboard;
